import * as React from 'react';
import { cn } from '@/core/lib/utils';
import { FileText, Image, File, Download, Trash2, ExternalLink } from 'lucide-react';

interface Attachment {
  id: string;
  name: string;
  type: string;
  size?: number;
  url?: string;
  thumbnailUrl?: string;
}

interface AttachmentListProps {
  attachments: Attachment[];
  onRemove?: (id: string) => void;
  onDownload?: (attachment: Attachment) => void;
  onPreview?: (attachment: Attachment) => void;
  readonly?: boolean;
  className?: string;
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function getFileIcon(type: string) {
  if (type.startsWith('image/')) return Image;
  if (type.includes('pdf')) return FileText;
  return File;
}

export function AttachmentList({
  attachments,
  onRemove,
  onDownload,
  onPreview,
  readonly = false,
  className,
}: AttachmentListProps) {
  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-2', className)}>
      {attachments.map((attachment) => {
        const Icon = getFileIcon(attachment.type);
        const isImage = attachment.type.startsWith('image/');

        return (
          <div
            key={attachment.id}
            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
          >
            {isImage && attachment.thumbnailUrl ? (
              <img
                src={attachment.thumbnailUrl}
                alt={attachment.name}
                className="h-10 w-10 object-cover rounded"
              />
            ) : (
              <div className="h-10 w-10 flex items-center justify-center bg-gray-200 rounded">
                <Icon className="h-5 w-5 text-gray-600" />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {attachment.name}
              </p>
              {attachment.size && (
                <p className="text-xs text-gray-500">
                  {formatFileSize(attachment.size)}
                </p>
              )}
            </div>

            <div className="flex items-center gap-1">
              {onPreview && (
                <button
                  type="button"
                  onClick={() => onPreview(attachment)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
                  title="Preview"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
              )}
              {onDownload && (
                <button
                  type="button"
                  onClick={() => onDownload(attachment)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </button>
              )}
              {!readonly && onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(attachment.id)}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                  title="Remove"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default AttachmentList;
